/**
 * Zone API 서비스
 * 구역 기반 좌석 선택 시스템을 위한 API 호출
 */
import api from './api';
import { useUserStore } from '../store/userStore';

export interface Zone {
  id: number;
  org_id: number;
  name: string; // "A구역", "조용한 구역"
  code: string; // "A", "QUIET"
  description?: string; // 구역 설명
  zone_type: string; // "general", "quiet", "premium", "study_room", "cafe", "group"
  layout_type: string; // "grid", "theater", "circular", "custom"
  floor_level: number; // 층수
  position_x: number; // 구역 위치 X 좌표
  position_y: number; // 구역 위치 Y 좌표
  width: number; // 구역 너비
  height: number; // 구역 높이
  max_rows: number; // 최대 행 수
  max_cols: number; // 최대 열 수
  base_hourly_rate: number; // 기본 시간당 요금 (Deprecated)
  base_daily_rate: number; // 기본 일일 요금 (Deprecated)
  hourly_surcharge?: number; // 시간당 추가 요금
  daily_surcharge?: number; // 일일 추가 요금
  is_active: boolean; // 사용 여부
  features?: Record<string, any>; // 추가 기능 정보
  display_color: string; // 구역 표시 색상 (hex)
  created_at: string;

  // 계산된 필드들
  total_seats: number;
  available_seats: number;
  occupied_seats: number;
  occupancy_rate: number;
}

export interface SeatInZone {
  id: string;
  seat_number: string; // "A-1", "B-2"
  seat_type: string; // "normal", "premium"
  row_number?: number; // 행 번호 (1, 2, 3...)
  col_number?: number; // 열 번호 (1, 2, 3...)
  row_label?: string; // 행 라벨 ("A", "B", "C")
  seat_label?: string; // 좌석 라벨 ("1", "2", "3")
  position_x: number; // 좌석 X 좌표
  position_y: number; // 좌석 Y 좌표
  direction?: string; // 좌석이 바라보는 방향 ("north", "south", "east", "west")
  status: string; // "available", "occupied", "reserved", "maintenance"
  is_available: boolean; // 선택 가능 여부
  hourly_rate: number; // 시간당 요금
  daily_rate: number; // 일일 요금
  current_user_id?: number;
  reserved_until?: string;
}

export interface ZoneWithSeats extends Zone {
  seats: SeatInZone[];
}

export interface ZoneLayoutXCircle {
  zone_id: number;
  zone_name: string;
  zone_type: string;
  layout_type: string;
  display_color: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  seats: SeatInZone[];
}

export interface StudyRoomLayout {
  floor_level: number;
  zones: ZoneLayoutXCircle[];
  total_capacity: number;
  available_capacity: number;
  occupancy_rate: number;
}

export interface ZoneSummary {
  zone_id: number;
  zone_name: string;
  zone_type: string;
  total_seats: number;
  available_seats: number;
  occupied_seats: number;
  reserved_seats: number;
  occupancy_rate: number;
  average_hourly_rate: number;
  features: Record<string, any>;
}

export interface ZoneCreate {
  name: string;
  code: string;
  description?: string;
  zone_type?: string;
  layout_type?: string;
  floor_level?: number;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  max_rows?: number;
  max_cols?: number;
  base_hourly_rate?: number;
  base_daily_rate?: number;
  hourly_surcharge?: number;
  daily_surcharge?: number;
  features?: Record<string, any>;
  display_color?: string;
}

export interface ZoneUpdate {
  name?: string;
  code?: string;
  description?: string;
  zone_type?: string;
  layout_type?: string;
  position_x?: number;
  position_y?: number;
  width?: number;
  height?: number;
  max_rows?: number;
  max_cols?: number;
  base_hourly_rate?: number;
  base_daily_rate?: number;
  hourly_surcharge?: number;
  daily_surcharge?: number;
  is_active?: boolean;
  features?: Record<string, any>;
  display_color?: string;
}

/**
 * Zone API 호출 함수들
 */
export const zoneApi = {
  /**
   * 모든 구역 목록 조회
   */
  getZones: async (params?: {
    skip?: number;
    limit?: number;
    zone_type?: string;
    is_active?: boolean;
  }): Promise<Zone[]> => {
    // 사용자의 organization_id 가져오기
    const userStore = useUserStore.getState();
    const orgId = userStore.currentUser?.org_id || userStore.currentUser?.business_id;
    
    // org_id를 파라미터에 추가
    const requestParams = {
      ...params,
      org_id: orgId
    };
    
    const response = await api.get('/v1/seat-layout/zones', { params: requestParams });
    return response.data;
  },

  /**
   * 특정 구역 정보 조회
   */
  getZone: async (zoneId: number): Promise<Zone> => {
    const response = await api.get(`/v1/seat-layout/zones/${zoneId}`);
    return response.data;
  },

  /**
   * 구역과 해당 구역의 모든 좌석 정보 조회
   */
  getZoneWithSeats: async (zoneId: number): Promise<ZoneWithSeats> => {
    // 사용자의 organization_id 가져오기
    const userStore = useUserStore.getState();
    const orgId = userStore.currentUser?.org_id || userStore.currentUser?.business_id;

    const response = await api.get(`/v1/seat-layout/zones/${zoneId}/seats`, {
      params: { org_id: orgId }
    });
    return response.data;
  },

  /**
   * 모든 구역의 현황 요약 조회
   */
  getZonesSummary: async (): Promise<ZoneSummary[]> => {
    // 사용자의 organization_id 가져오기
    const userStore = useUserStore.getState();
    const orgId = userStore.currentUser?.org_id || userStore.currentUser?.business_id;

    const response = await api.get('/v1/seat-layout/zones/summary', {
      params: { org_id: orgId }
    });
    return response.data;
  },

  /**
   * 특정 층의 전체 독서실 배치도 조회
   */
  getFloorLayout: async (floorLevel: number): Promise<StudyRoomLayout> => {
    const response = await api.get(`/v1/seat-layout/layout/floor/${floorLevel}`);
    return response.data;
  },

  /**
   * 새 구역 생성 (관리자 전용)
   */
  createZone: async (zoneData: ZoneCreate): Promise<Zone> => {
    const response = await api.post('/v1/seat-layout/zones', zoneData);
    return response.data;
  },

  /**
   * 구역 정보 수정 (관리자 전용)
   */
  updateZone: async (zoneId: number, zoneData: ZoneUpdate): Promise<Zone> => {
    const response = await api.put(`/v1/seat-layout/zones/${zoneId}`, zoneData);
    return response.data;
  },

  /**
   * 구역 삭제 (관리자 전용)
   */
  deleteZone: async (zoneId: number): Promise<void> => {
    await api.delete(`/v1/seat-layout/zones/${zoneId}`);
  },
};

/**
 * 구역 타입별 한글 표시명
 */
export const ZONE_TYPE_LABELS: Record<string, string> = {
  general: '일반석',
  quiet: '조용한 구역',
  premium: '프리미엄석',
  study_room: '스터디룸',
  cafe: '카페 구역',
  group: '그룹 스터디',
};

/**
 * 레이아웃 타입별 한글 표시명
 */
export const LAYOUT_TYPE_LABELS: Record<string, string> = {
  grid: '격자형',
  theater: '영화관식',
  circular: '원형 배치',
  custom: '커스텀',
};

/**
 * 좌석 상태별 색상 및 표시명
 */
export const SEAT_STATUS_CONFIG: Record<
  string,
  {
    label: string;
    color: string;
    bgColor: string;
    icon: string;
  }
> = {
  available: {
    label: '선택 가능',
    color: '#10B981', // green-500
    bgColor: '#D1FAE5', // green-100
    icon: '🟢',
  },
  occupied: {
    label: '사용 중',
    color: '#EF4444', // red-500
    bgColor: '#FEE2E2', // red-100
    icon: '🔴',
  },
  reserved: {
    label: '예약 중',
    color: '#F59E0B', // amber-500
    bgColor: '#FEF3C7', // amber-100
    icon: '🟡',
  },
  maintenance: {
    label: '점검 중',
    color: '#6B7280', // gray-500
    bgColor: '#F3F4F6', // gray-100
    icon: '🚫',
  },
};
